import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ShoppingBag, ArrowRight, Ruler } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";

export default function Cart() {
  const { cart, removeFromCart, cartTotal, clearCart, fitProfile } = useShop();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    setBusy(true);
    try {
      const items = cart.map((i) => ({
        slug: i.slug, quantity: i.qty || 1, color: i.color, fabric: i.fabric, size: i.size,
      }));
      const { data } = await api.post("/payments/checkout", { items, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout could not start. Please try again.");
      setBusy(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div data-testid="cart-empty" className="pt-40 pb-32 text-center min-h-screen">
        <ShoppingBag size={40} strokeWidth={1} className="mx-auto text-charcoal/30 mb-6" />
        <h1 className="font-serif text-4xl font-light">Your bag is empty</h1>
        <p className="text-charcoal/55 mt-3">The sea is calling. Find your fit and choose a piece.</p>
        <Link to="/women" data-testid="empty-shop-cta" className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline mt-8 hover:opacity-90 transition-opacity">
          Explore the atelier <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const currency = cart[0].currency || "CHF";

  return (
    <div data-testid="cart-page" className="pt-28 pb-32 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-10">Your bag</h1>
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {cart.map((item) => (
              <div key={item.lineId} data-testid={`cart-item-${item.slug}`} className="flex gap-5 border-b border-charcoal/10 pb-6">
                <img src={item.image} alt={item.name} className="w-24 h-32 object-cover rounded-xl bg-linen" />
                <div className="flex-1">
                  <div className="flex justify-between gap-4">
                    <h3 className="font-serif text-xl">{item.name}</h3>
                    <span className="font-sans text-sm">{item.currency} {item.price}</span>
                  </div>
                  <div className="text-sm text-charcoal/55 mt-1 space-y-0.5">
                    <div>Colour: {item.color} · Fabric: {item.fabric}</div>
                    <div className="flex items-center gap-1.5">
                      <Ruler size={13} /> {item.madeToMeasure ? `Made to measure · Size ${item.size}` : `Size ${item.size}`}
                    </div>
                  </div>
                  <button
                    data-testid={`remove-${item.slug}`}
                    onClick={() => { removeFromCart(item.lineId); toast("Removed from bag"); }}
                    className="text-charcoal/50 hover:text-destructive transition-colors inline-flex items-center gap-1.5 text-xs mt-3"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-linen/60 border border-charcoal/10 rounded-2xl p-7 sticky top-28">
              <div className="overline mb-5">Summary</div>
              {!fitProfile && (
                <Link to="/fit-studio" className="flex items-center gap-2 text-xs text-charcoal/70 mb-5 hover:text-charcoal transition-colors" data-testid="cart-fit-link">
                  <Ruler size={14} className="text-sunkissed" /> Add your fit profile for a made-to-measure cut
                </Link>
              )}
              <div className="flex justify-between text-sm mb-3">
                <span className="text-charcoal/60">Subtotal</span>
                <span>{currency} {cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-5">
                <span className="text-charcoal/60">Tailoring &amp; shipping</span>
                <span>Included</span>
              </div>
              <div className="flex justify-between font-serif text-xl border-t border-charcoal/15 pt-4 mb-6">
                <span>Total</span>
                <span data-testid="cart-total">{currency} {cartTotal.toFixed(2)}</span>
              </div>
              <button
                data-testid="checkout-btn"
                disabled={busy}
                onClick={checkout}
                className="w-full bg-ocean text-sand py-4 overline hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {busy ? "Redirecting to secure checkout…" : "Proceed to checkout"}
              </button>
              <button data-testid="clear-cart" onClick={clearCart} className="w-full text-xs text-charcoal/45 hover:text-charcoal mt-4 transition-colors">
                Clear bag
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
